import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { organizationsApi } from '@/services/api';
import type { Organization, OrgPublicInfo } from '@/types';

type Social = NonNullable<OrgPublicInfo['social_media']>;
type Address = NonNullable<OrgPublicInfo['address']>;

const SOCIAL: { key: keyof Social; label: string }[] = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'twitter', label: 'X (Twitter)' },
  { key: 'tiktok', label: 'TikTok' },
];
const ADDRESS: { key: keyof Address; label: string; wide?: boolean }[] = [
  { key: 'street', label: 'Calle y número', wide: true },
  { key: 'zip_code', label: 'Código postal' },
  { key: 'city', label: 'Localidad' },
  { key: 'state', label: 'Provincia' },
  { key: 'country', label: 'País' },
];

/**
 * Datos de contacto públicos de la organización (VIDA-359). Los muestran las webs
 * de catálogo y tienda; su email recibe los mensajes del contacto del catálogo
 * cuando la organización no tiene CRM.
 */
export default function PublicInfoEditor({ org, onSaved }: { org: Organization; onSaved: (org: Organization) => void }) {
  const [info, setInfo] = useState<OrgPublicInfo>(org.config?.public_info || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => { setInfo(org.config?.public_info || {}); }, [org._id, org.config?.public_info]);

  const address = info.address || {};
  const social = info.social_media || {};
  const dirty = JSON.stringify(info) !== JSON.stringify(org.config?.public_info || {});

  const save = async () => {
    setBusy(true);
    try {
      await organizationsApi.update(org._id, { config: { ...org.config, public_info: info } });
      const response = await organizationsApi.get(org._id);
      onSaved(response.data.organization);
      toast.success('Datos públicos guardados');
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string; details?: { message?: string }[] } } })?.response?.data;
      toast.error(message?.details?.[0]?.message || message?.error || 'No se pudieron guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:col-span-2 border border-gray-200 rounded-lg p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="label mb-0">Datos de contacto públicos</p>
          <p className="text-xs text-gray-500 mt-1">
            Los muestran las webs de catálogo y tienda. El email recibe los mensajes del formulario de contacto del
            catálogo si la organización no tiene CRM.
          </p>
        </div>
        <button type="button" onClick={save} disabled={busy || !dirty} className="btn-primary text-sm disabled:opacity-50">
          {busy ? 'Guardando…' : 'Guardar datos públicos'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div className="md:col-span-2">
          <label className="label" htmlFor="pi-description">Descripción</label>
          <textarea id="pi-description" className="input min-h-[64px]" maxLength={2000}
            value={info.description || ''} onChange={(e) => setInfo({ ...info, description: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="pi-email">Email</label>
          <input id="pi-email" type="email" className="input" maxLength={200}
            value={info.email || ''} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="pi-phone">Teléfono</label>
          <input id="pi-phone" className="input" maxLength={60}
            value={info.phone || ''} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
        </div>
        {ADDRESS.map((f) => (
          <div key={f.key} className={f.wide ? 'md:col-span-2' : undefined}>
            <label className="label" htmlFor={`pi-${f.key}`}>{f.label}</label>
            <input id={`pi-${f.key}`} className="input" maxLength={200}
              value={address[f.key] || ''} onChange={(e) => setInfo({ ...info, address: { ...address, [f.key]: e.target.value } })} />
          </div>
        ))}
        {SOCIAL.map((f) => (
          <div key={f.key}>
            <label className="label" htmlFor={`pi-${f.key}`}>{f.label}</label>
            <input id={`pi-${f.key}`} className="input" placeholder="https://" maxLength={300}
              value={social[f.key] || ''} onChange={(e) => setInfo({ ...info, social_media: { ...social, [f.key]: e.target.value } })} />
          </div>
        ))}
      </div>
    </div>
  );
}
